import type { CityConfig } from "../city-template/types";

export const oceanCityConfig: CityConfig = {
  city: "Ocean City",
  state: "Maryland",
  stateAbbr: "MD",
  slug: "ocean-city",
  route: "/cities/ocean-city",
  title: "Emergency Passport Ocean City MD | Expedited Passport Processing | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Ocean City, Maryland — as fast as 24 hours. Fast passport renewal and new passport applications for Ocean City, Berlin, and Ocean Pines residents. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Ocean City, MD",
  heroSubtitle: "Ocean City travelers — from the boardwalk to the bay to the family beach cottages — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated courier service serving Worcester County and the Eastern Shore.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20of%20Ocean%20City%20Maryland%20beach%20resort%20with%20golden%20sandy%20beaches%2C%20colorful%20beach%20umbrellas%2C%20the%20iconic%20boardwalk%2C%20Atlantic%20Ocean%20waves%2C%20and%20coastal%20town%20buildings%2C%20bright%20summer%20sunshine%20with%20turquoise%20water%2C%20vibrant%20coastal%20resort%20atmosphere%2C%20professional%20aerial%20photography%2C%20no%20visible%20text%20or%20people%2C%20warm%20golden%20and%20blue%20tones&width=1800&height=700&seq=ocean-city-hero-001&orientation=landscape",
  heroImageAlt: "Aerial view of Ocean City Maryland beach resort with golden sandy beaches and the iconic boardwalk",
  geoPosition: "38.3365;-75.0849",
  geoPlacename: "Ocean City, Maryland",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Washington DC Passport Agency",
    address: "600 19th St NW, 1st Floor, Washington, DC 20006",
    distance: "Approximately 2.5–3 hours from Ocean City via US-50 W",
  },
  nearbyCitiesZips: [
    { name: "Ocean City", zips: "21842, 21843" },
    { name: "Berlin", zips: "21811" },
    { name: "Ocean Pines", zips: "21811" },
    { name: "West Ocean City", zips: "21842" },
    { name: "Fenwick Island", zips: "19944" },
    { name: "Bethany Beach", zips: "19930" },
    { name: "Snow Hill", zips: "21863" },
    { name: "Pocomoke City", zips: "21851" },
    { name: "Salisbury", zips: "21801, 21802, 21803, 21804" },
    { name: "Fruitland", zips: "21826" },
    { name: "Delmar", zips: "21875" },
    { name: "Princess Anne", zips: "21853" },
    { name: "Crisfield", zips: "21817" },
    { name: "Assateague Island", zips: "21811" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for Ocean City and Eastern Shore departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-ship-line", title: "Cruise & Resort Travel", desc: "Serving cruise passengers and beach resort travelers with tight departure schedules" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no long drive to DC or Baltimore" },
  ],
  internalLinks: [
    { name: "Expedited passport in Salisbury, MD", route: "/cities/salisbury-md" },
    { name: "Emergency passport help in Berlin, MD", route: "/cities/berlin" },
    { name: "Urgent passport services in Easton, MD", route: "/cities/easton" },
    { name: "Maryland passport services overview", route: "/states/maryland" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Ocean City, Maryland remotely via secure registered courier. We do not have a walk-in office in Ocean City — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Worcester County travelers without the long drive to downtown DC.",
  fpcOfficeDistance: "Serving Ocean City, Berlin, Ocean Pines, and greater Worcester County and the Eastern Shore",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the Ocean City area and Worcester County. From the bustling boardwalk and beachfront hotels to the quiet bay-side neighborhoods, from the inland communities of Berlin and Snow Hill to the Delaware coastal towns — we serve all of the Eastern Shore's travelers.",
  nearbyCitiesIntro: "We serve Ocean City and nearby communities including Berlin, Ocean Pines, West Ocean City, Fenwick Island, Bethany Beach, Snow Hill, Pocomoke City, Salisbury, Fruitland, Delmar, Princess Anne, and Crisfield. We also serve Sussex County Delaware residents and Assateague Island visitors.",
  howItWorksIntro: "Most Ocean City travelers complete everything online and ship documents overnight. No need to drive 2.5–3 hours to downtown DC or battle summer beach traffic on US-50.",
  mistakesIntro: "Ocean City is a popular beach resort and cruise departure area with seasonal travel spikes. Here are the most common passport mistakes we see from Eastern Shore travelers.",
  testimonialsIntro: "Ocean City travelers — from cruise passengers to beach families to seasonal workers — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Ocean City, MD? Here are the most common ones we hear from Worcester County and Eastern Shore travelers.",
  statePageRoute: "/states/maryland",
  statePageName: "Maryland",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 2.5–3 hours to downtown DC", "Full day commitment — impractical for Eastern Shore residents"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Perfect for cruise and resort travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Ocean City travelers with immediate departure needs — as fast as 24 hours. Perfect for last-minute cruise departures, beach resort trips, and family emergencies.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Cruise and resort travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Ocean City adults with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to Ocean City address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Ocean City residents and families — including seasonal workers and beach resort staff.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Ocean City residents who lost their passport before travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Ocean City families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Ocean City residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Ocean City MD", description: "Urgent passport processing for Ocean City Maryland travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Ocean City MD", description: "Fast passport renewal for Ocean City Maryland residents with a previously-issued passport." },
    { name: "First-Time Passport Ocean City MD", description: "New passport application services for Ocean City Maryland residents applying for their first passport." },
    { name: "Child Passport Ocean City MD", description: "Minor passport services for Ocean City Maryland families with children under 16." },
  ],
  testimonials: [
    {
      name: "The Johnson Family",
      location: "Ocean City, MD",
      rating: 5,
      text: "Booked a Caribbean cruise with 2 weeks notice and realized my daughter's passport expired. Fast Passport Center handled her renewal in 4 days. We boarded the ship with zero stress. Absolutely saved our family vacation.",
      service: "Child Passport Renewal",
    },
    {
      name: "Mike S.",
      location: "Berlin, MD",
      rating: 5,
      text: "Seasonal worker heading to the Bahamas for the winter season. Needed my passport renewed in under a week. Fast Passport Center delivered in 3 days. The overnight shipping meant I never had to leave the Eastern Shore.",
      service: "Passport Renewal",
    },
    {
      name: "Karen D.",
      location: "Ocean Pines, MD",
      rating: 5,
      text: "Last-minute honeymoon trip to Aruba with 10 days notice. Fast Passport Center reviewed everything, caught a form error, and had our passports ready in 5 days. Perfect service for busy professionals planning around work schedules.",
      service: "Passport Renewal",
    },
    {
      name: "Robert T.",
      location: "West Ocean City, MD",
      rating: 5,
      text: "Lost my passport the week before a fishing trip to Costa Rica. I was devastated. Fast Passport Center walked me through the replacement process and had it in my hands in 48 hours. Truly saved our trip.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "ocean-city-1",
      name: "Ocean City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "12741 Ocean Gateway",
      city: "Ocean City",
      state: "MD",
      zip: "21842",
      phone: "(410) 289-8111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Central Ocean City, near the boardwalk",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Ocean City acceptance facility. Busy during summer season — book appointments well in advance. Limited parking during peak season.",
      icon: "ri-mail-line",
    },
    {
      id: "ocean-city-2",
      name: "Berlin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10 S Main St",
      city: "Berlin",
      state: "MD",
      zip: "21811",
      phone: "(410) 641-2220",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Berlin, approximately 8 miles west of Ocean City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Berlin, Ocean Pines, and inland Worcester County residents. Less crowded than Ocean City location during summer.",
      icon: "ri-mail-line",
    },
    {
      id: "ocean-city-3",
      name: "Salisbury Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1822 N Salisbury Blvd",
      city: "Salisbury",
      state: "MD",
      zip: "21801",
      phone: "(410) 860-5000",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Salisbury, approximately 30 miles west of Ocean City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serving Wicomico County and western Worcester County. Good alternative for Salisbury, Fruitland, and Delmar residents.",
      icon: "ri-mail-line",
    },
    {
      id: "ocean-city-4",
      name: "Worcester County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "1 W Market St",
      city: "Snow Hill",
      state: "MD",
      zip: "21863",
      phone: "(410) 632-1340",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Snow Hill, approximately 20 miles southwest of Ocean City",
      website: "https://www.worcestercountymd.gov/",
      websiteLabel: "Worcester County",
      notes: "County-level facility with full passport acceptance. Extended hours compared to post office locations. Accessible via US-113.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited DC slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 2.5–3 hour drive to downtown DC", fpc: "No — ship from Ocean City" },
    { feature: "Cruise Travel Support", agency: "No flexibility for cruise deadlines", fpc: "Yes — we handle cruise passenger cases" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Ocean City's seasonal travelers often book cruises and international trips on short notice. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local photo services on the Eastern Shore sometimes produce photos that meet general ID requirements but not passport standards. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Worcester County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Booking the Acceptance Facility Early",
      description: "Ocean City Post Office appointments fill up extremely fast during summer cruise season and holiday travel periods. Book 3–4 weeks ahead.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Always verify current fees before submitting — incorrect payment means starting over.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Routine Processing Time",
      description: "Ocean City residents often assume 4–6 weeks is enough. Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your beach house, condo, or hotel in Ocean City or Berlin." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx location along Coastal Highway or in downtown Berlin." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Ocean City address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Ocean City, MD?", a: "Fast Passport Center offers emergency passport processing in Ocean City, Maryland in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Washington DC Passport Agency — without you needing to make the 2.5–3 hour drive across the Chesapeake Bay." },
    { q: "Does Ocean City have its own passport agency?", a: "No — neither Ocean City nor the Eastern Shore has a regional passport agency. The closest facility is the Washington DC Passport Agency, approximately 2.5–3 hours from Ocean City via US-50. Fast Passport Center's courier service means Eastern Shore residents never need to make that long drive." },
    { q: "Can I get expedited passport services if I live in Berlin or Ocean Pines?", a: "Absolutely. Fast Passport Center serves all of Worcester County and the Eastern Shore including Berlin, Ocean Pines, West Ocean City, Snow Hill, Pocomoke City, and Salisbury. Our service works the same way — complete your application online, ship your documents, and we handle the rest." },
    { q: "How much does an expedited passport cost in Ocean City, MD?", a: "Government fees are fixed: $130 for a first-time passport book or $130 + $60 expedite for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your departure timeline." },
    { q: "What airports do Ocean City travelers use for international flights?", a: "Most Ocean City residents fly internationally through Salisbury Regional Airport (SBY), approximately 30 minutes west via US-50, with connections to Charlotte and Philadelphia. For major international flights, Baltimore-Washington International (BWI) is approximately 2.5 hours west via US-50. Philadelphia International (PHL) is approximately 2.5 hours north. Always verify your passport is valid for at least 6 months past your return date." },
    { q: "Can I get a passport for my whole family in Ocean City?", a: "Yes. Fast Passport Center handles multi-passport cases for families in Ocean City, Berlin, Ocean Pines, and surrounding areas. Whether you need child passports, renewals, or first-time applications for the whole family, we coordinate the process to ensure everyone is ready to travel — especially important for cruise departures." },
    { q: "Where is the closest passport acceptance facility to Ocean City?", a: "The Ocean City Post Office at 12741 Ocean Gateway is the primary acceptance facility. The Berlin Post Office at 10 S Main St serves inland Worcester County. The Salisbury Post Office at 1822 N Salisbury Blvd serves Wicomico County. All require appointments — book well ahead during summer season." },
    { q: "How do I know if I need expedited or routine passport processing?", a: "If your travel is within 10 weeks, use expedited service. If your travel is within 2–3 weeks, use emergency expedited service. Routine processing takes 6–13 weeks and is only suitable for travel more than 3 months away. Ocean City residents should plan ahead, especially for cruise departures and winter travel to warmer destinations." },
  ],
  photoLocations: [
    { name: "Walgreens — Ocean City", address: "12301 Coastal Hwy, Ocean City, MD 21842", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Ocean City", address: "11001 Coastal Hwy, Ocean City, MD 21842", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Salisbury", address: "1406 S Salisbury Blvd, Salisbury, MD 21801", hours: "Mon–Sun: 7AM–10PM" },
    { name: "UPS Store — Berlin", address: "10141 Old Ocean City Blvd, Berlin, MD 21811", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
  ],
  dmvLocations: [
    { name: "MVA — Salisbury", address: "2514 N Salisbury Blvd, Salisbury, MD 21801", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Closest full-service MVA to Ocean City and the Eastern Shore. REAL ID processing available. Appointments recommended.", featured: true },
    { name: "MVA — Easton", address: "9140 Centreville Rd, Easton, MD 21601", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Alternative MVA for Talbot County and mid-Shore residents. REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "SBY",
      name: "Salisbury Regional",
      distance: "30 minutes from Ocean City via US-50",
      address: "5485 Airport Terminal Rd, Salisbury, MD 21804",
      note: "Regional airport with connections to Charlotte (CLT) and Philadelphia (PHL) for international connections. American Airlines primary carrier.",
      flightUrl: "https://www.flysbyairport.com/",
      flightLabel: "SBY Flight Info",
      featured: true,
    },
    {
      code: "BWI",
      name: "Baltimore-Washington International Thurgood Marshall",
      distance: "2.5 hours from Ocean City via US-50",
      address: "7050 Friendship Rd, Baltimore, MD 21240",
      note: "Major international departure airport for Eastern Shore travelers with direct flights to Europe, Caribbean, and Central America. Southwest Airlines hub.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Flight Info",
      featured: false,
    },
    {
      code: "PHL",
      name: "Philadelphia International",
      distance: "2.5 hours from Ocean City via DE-1 N",
      address: "8000 Essington Ave, Philadelphia, PA 19153",
      note: "Alternative for long-haul international flights to Europe and Asia. American Airlines hub with extensive international connections.",
      flightUrl: "https://www.phl.org/",
      flightLabel: "PHL Flight Info",
      featured: false,
    },
  ],
};